import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { fg } from '@confluence/feature-gating';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import { getIsLoomDisabled } from './getIsLoomDisabled.ts';
import { getIsLoomBetaEnabled } from './getIsLoomBetaEnabled.ts';
import type {
	LoomEntryPointVariant,
	LoomMeetingRecorderEligibility,
} from './useLoomEntryPointVariantState.tsx';
import {
	LoomTenantProvisionStatus,
	LoomUserPermissionStatus,
} from './useLoomEntryPointVariantState.tsx';
import {
	LoomUserPermissionQuery,
	getLoomUserPermissionQueryVariables,
	hasExactLoomUserPermission,
} from './loomUserPermission.ts';
import UseLoomTenantProvisionStatusQuery, {
	type useLoomTenantProvisionStatusQuery$data,
	type useLoomTenantProvisionStatusQuery$variables,
} from './__generated__/useLoomTenantProvisionStatusQuery.graphql.ts';
import UseIsLoomEntryPointsEnabledQuery, {
	type useIsLoomEntryPointsEnabledQuery$data,
	type useIsLoomEntryPointsEnabledQuery$variables,
} from './__generated__/useIsLoomEntryPointsEnabledQuery.graphql.ts';
import type {
	loomUserPermissionQuery$data,
	loomUserPermissionQuery$variables,
} from './__generated__/loomUserPermissionQuery.graphql.ts';
import { createAtlAttributionHeader } from './utils/createAtlAttributionHeader.ts';
import { isMeetingRecorderEligibilityEnabled } from './isMeetingRecorderEligibilityEnabled.ts';
import {
	type LoomPersonalizationInputs,
	type LoomPersonalizationResult,
	preloadLoomPersonalizationPermission,
} from './loomPersonalization.ts';

declare global {
	interface Window {
		__SSR_LOOM_ENTRY_POINT__?: LoomEntryPointVariant | null;
	}
}

export type preloadRecordLoomButtonProps = {
	cloudId: string;
	activationId: string;
	accessStatus: AccessStatus;
	isLoggedIn: boolean;
	userId: string | null;
	/** Raw route context; other Loom callers retain their existing loading path. */
	isViewPage?: boolean;
	personalizationInputs?: Promise<LoomPersonalizationResult>;
};

/*
 * Preloader function to determine entrypoint variant, which follows the same logic in next/packages/loom-utils/src/useLoomEntryPointVariantState.tsx.
 * The function is replicated here because hooks do not execute during SSR (including useIsLoomDisabled/useIsLoomBetaEnabled) and there is no access to the central state
 *
 * NOTE: CHANGES TO THIS LOGIC MUST ALSO BE REPLICATED IN next/packages/loom-utils/src/useLoomEntryPointVariantState.tsx TO ENSURE THE CORRECT EXPERIENCE DURING SSR
 */

export async function preloadRecordLoomButton({
	cloudId,
	activationId,
	accessStatus,
	isLoggedIn,
	userId,
	isViewPage = false,
	personalizationInputs,
}: preloadRecordLoomButtonProps): Promise<any> {
	const isLoomDisabled = getIsLoomDisabled({ accessStatus, isLoggedIn });
	const isBetaEnabled = getIsLoomBetaEnabled(accessStatus);

	// A failed or excluded parent restores the two legacy loaders. A successful parent owns
	// both inputs, so neither standalone operation runs.
	const parentResult = await personalizationInputs?.catch(() => ({ retryLegacy: true as const }));
	const inputs = parentResult && 'entryPoints' in parentResult ? parentResult : undefined;
	// A failed parent may have normalized partial data. Re-establish independent error boundaries
	// over the network instead of treating those partial records as a successful legacy cache hit.
	const fetchPolicy =
		parentResult && 'retryLegacy' in parentResult ? 'network-only' : 'cache-first';
	const [isLoomEntryPointsEnabledResult, licensedProductsQueryResult] = await Promise.allSettled([
		preloadIsLoomEntryPointsEnabledQuery(
			cloudId,
			activationId,
			accessStatus,
			fetchPolicy,
			inputs?.entryPoints,
		),
		inputs
			? Promise.resolve(inputs.tenant)
			: query<useLoomTenantProvisionStatusQuery$data, useLoomTenantProvisionStatusQuery$variables>({
					query: UseLoomTenantProvisionStatusQuery,
					...(fetchPolicy === 'network-only' ? { fetchPolicy } : {}),
				}),
	]);

	const loomConfiguration =
		isLoomEntryPointsEnabledResult.status === 'fulfilled'
			? isLoomEntryPointsEnabledResult.value
			: { isLoomEntryPointsEnabled: true };
	const isLoomEntryPointsEnabled = loomConfiguration?.isLoomEntryPointsEnabled;
	const meetingRecorderEligibility = loomConfiguration?.meetingRecorderEligibility;
	const setPreloadedEntryPoint = (variant: LoomEntryPointVariant) => {
		window['__SSR_LOOM_ENTRY_POINT__'] = meetingRecorderEligibility
			? { ...variant, meetingRecorderEligibility }
			: variant;
	};

	if (window['__SSR_LOOM_ENTRY_POINT__']) {
		return Promise.resolve();
	}

	if (isLoomDisabled || !isLoomEntryPointsEnabled) {
		setPreloadedEntryPoint({ entryPointVariant: 'DISABLED' });
		return Promise.resolve();
	}

	if (isBetaEnabled) {
		setPreloadedEntryPoint({ entryPointVariant: 'BETA' });
		return Promise.resolve();
	}

	// For now, we have to do the Beta check first because `confluence_frontend_loom_cross_sell` is
	// not enabled on Beta sites
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	if (!fg('confluence_frontend_loom_cross_sell')) {
		setPreloadedEntryPoint({ entryPointVariant: 'DISABLED' });
		return Promise.resolve();
	}

	if (licensedProductsQueryResult.status === 'fulfilled') {
		const licenseQueryData = licensedProductsQueryResult.value.data;

		const isTenantLicensed = licenseQueryData?.confluence_tenantContext?.licensedProducts.some(
			(product) => product.productKey === 'loom' && product.licenseStatus === 'ACTIVE',
		);

		const loomTenantProvisionStatus = isTenantLicensed
			? LoomTenantProvisionStatus.PROVISIONED
			: LoomTenantProvisionStatus.NOT_PROVISIONED;

		if (loomTenantProvisionStatus === LoomTenantProvisionStatus.NOT_PROVISIONED) {
			setPreloadedEntryPoint({ entryPointVariant: 'CROSS_FLOW' });

			// if entry point variant is "CROSS_FLOW" but FF is enabled, return "DISABLED"
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			if (fg('confluence_frontend_loom_cross_flow_ads_opt_out')) {
				setPreloadedEntryPoint({ entryPointVariant: 'DISABLED' });
			}
			return Promise.resolve();
		}
	} else {
		setPreloadedEntryPoint({ entryPointVariant: 'CROSS_FLOW', error: true });

		// if entry point variant is "CROSS_FLOW" but FF is enabled, return "DISABLED"
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		if (fg('confluence_frontend_loom_cross_flow_ads_opt_out')) {
			setPreloadedEntryPoint({ entryPointVariant: 'DISABLED' });
		}
		return Promise.resolve();
	}
	// tenant is provisioned at this point, switch to "CROSS_JOIN" and attempt permissions check
	setPreloadedEntryPoint({ entryPointVariant: 'CROSS_JOIN' });

	// PERMISSIONS CHECK
	const loomUserPermissionStatus = await preloadLoomUserPermissionStatus(
		cloudId,
		userId,
		activationId,
		isLoggedIn,
		isViewPage,
	);

	if (loomUserPermissionStatus === LoomUserPermissionStatus.NOT_PERMITTED) {
		// if not permitted, entryPointVariant is already 'CROSS_JOIN' so resolve promise
		return Promise.resolve();
	}

	if (loomUserPermissionStatus === LoomUserPermissionStatus.ERROR) {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (Boolean(process.env.REACT_SSR) && Boolean(process.env.BIFROST_TEMPLATE_BUILD)) {
			window['__SSR_LOOM_ENTRY_POINT__'] = null;
		} else {
			setPreloadedEntryPoint({ entryPointVariant: 'CROSS_JOIN', error: true });
		}
		return Promise.resolve();
	}

	setPreloadedEntryPoint({ entryPointVariant: 'CO_USE' });
	return Promise.resolve();
}

const preloadLoomUserPermissionStatus = async (
	cloudId: string,
	userId: string | null,
	activationId: string,
	isLoggedIn: boolean,
	isViewPage: boolean,
): Promise<LoomUserPermissionStatus> => {
	if (!userId) {
		return LoomUserPermissionStatus.NOT_PERMITTED;
	}

	const variables = getLoomUserPermissionQueryVariables(cloudId, userId);
	try {
		const { data, hasErrors } = await (isViewPage &&
		isLoggedIn &&
		window.GLOBAL_RELAY_ENV &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_loom_reader') &&
		expVal('confluence_exp_personalization_contract', 'isEnabled', false)
			? preloadLoomPersonalizationPermission(variables, cloudId, activationId)
			: queryAgg<loomUserPermissionQuery$data, loomUserPermissionQuery$variables>({
					query: LoomUserPermissionQuery,
					variables,
					context: {
						headers: {
							...createAtlAttributionHeader(cloudId, activationId),
						},
					},
					errorPolicy: 'all',
				}));

		if (hasErrors) {
			return LoomUserPermissionStatus.ERROR;
		}

		return hasExactLoomUserPermission(data, variables)
			? LoomUserPermissionStatus.PERMITTED
			: LoomUserPermissionStatus.NOT_PERMITTED;
	} catch (e) {
		getMonitoringClient().submitError(e, {
			attribution: Attribution.CONTENT_TYPES,
		});
		markErrorAsHandled(e);
		return LoomUserPermissionStatus.ERROR;
	}
};

const preloadIsLoomEntryPointsEnabledQuery = async (
	cloudId: string,
	activationId: string,
	accessStatus: AccessStatus,
	fetchPolicy: 'cache-first' | 'network-only',
	parentEntryPoints?: LoomPersonalizationInputs['entryPoints'],
): Promise<
	| {
			isLoomEntryPointsEnabled: boolean | undefined;
			meetingRecorderEligibility?: LoomMeetingRecorderEligibility;
	  }
	| undefined
> => {
	const shouldSkip = !(
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS
	);

	if (shouldSkip) {
		return;
	}

	const includeMeetingRecorderEligibility = isMeetingRecorderEligibilityEnabled();
	const { data, hasErrors } = await (parentEntryPoints ??
		queryAgg<useIsLoomEntryPointsEnabledQuery$data, useIsLoomEntryPointsEnabledQuery$variables>({
			query: UseIsLoomEntryPointsEnabledQuery,
			...(fetchPolicy === 'network-only' ? { fetchPolicy } : {}),
			variables: {
				cloudId,
				includeMeetingRecorderEligibility,
			},
			context: {
				headers: {
					...createAtlAttributionHeader(cloudId, activationId),
				},
			},
			errorPolicy: 'all',
		}));

	if (hasErrors) {
		return { isLoomEntryPointsEnabled: true };
	}

	return {
		isLoomEntryPointsEnabled: data?.confluence_loomEntryPoints?.isLoomEntryPointsEnabled,
		meetingRecorderEligibility: includeMeetingRecorderEligibility
			? {
					meetingRecordingsAvailable: data?.loom_settings?.meetingRecordingsAvailable,
					meetingNotesEligible: data?.loom_settings?.meetingNotesEligible,
					connectedCalendars: data?.loom_connectedCalendars,
				}
			: undefined,
	};
};
